import React, { useState, useEffect } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Button,
    Box,
    Typography,
    IconButton,
    CircularProgress,
    List,
    ListItem,
    ListItemText,
    ListItemSecondaryAction,
    Divider,
    Alert
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import { useDispatch, useSelector } from "react-redux";
import { fetchStaffs, clearStaffCache } from "./staffSlice";
import { createStaffAccount } from "./onboardingSlice";
import { useAdminAuth } from "../../hooks/useAdminAuth";
import { toast } from "react-toastify";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import InputAdornment from "@mui/material/InputAdornment";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import EmailIcon from "@mui/icons-material/Email";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";

const MobileStaffDialog = ({ isOpen, onClose }) => {
    const dispatch = useDispatch();
    const { user } = useSelector((state) => state.auth);
    const { staffs, loading, lastFetched } = useSelector((state) => state.staff);
    const { linkStaff, deleteStaff } = useAdminAuth();

    // View state: 'list', 'add', 'delete', or 'success'
    const [currentView, setCurrentView] = useState('list');
    const [staffBranchName, setStaffBranchName] = useState("");
    const [staffEmail, setStaffEmail] = useState("");
    const [staffPassword, setStaffPassword] = useState("");
    const [staffWhatsApp, setStaffWhatsApp] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const [actionLoading, setActionLoading] = useState(false);
    const [error, setError] = useState("");
    const [staffToDelete, setStaffToDelete] = useState(null);
    const [createdStaffInfo, setCreatedStaffInfo] = useState(null);
    const [sendingInvitations, setSendingInvitations] = useState(false);
    const [isEmailLoading, setIsEmailLoading] = useState(false);

    // Reset to list view when dialog opens
    useEffect(() => {
        if (isOpen) {
            setCurrentView('list');
        }
    }, [isOpen]);

    // Fetch staff list when dialog opens (if needed)
    useEffect(() => {
        if (isOpen && user?.uid && staffs.length === 0 && !loading && !lastFetched) {
            console.log('MobileStaffDialog: Fetching staffs for admin:', user.uid);
            dispatch(fetchStaffs(user.uid));
        }
    }, [isOpen, user?.uid, dispatch, staffs.length, loading, lastFetched]);

    const handleCreateStaff = async () => {
        if (staffs.length >= 5) {
            setError("Limit Exceeded: You can only create up to 5 staff accounts.");
            return;
        }
        if (!staffBranchName.trim()) {
            setError("Please enter a branch name");
            return;
        }
        if (!staffEmail.trim()) {
            setError("Please enter an email address");
            return;
        }

        if (!staffPassword || staffPassword.length < 6) {
            setError("Password must be at least 6 characters");
            return;
        }

        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(staffEmail)) {
            setError("Please enter a valid email address");
            return;
        }

        if (staffWhatsApp.trim() && !/^\d{10}$/.test(staffWhatsApp.trim())) {
            setError("WhatsApp number must be exactly 10 digits");
            return;
        }

        setActionLoading(true);
        setError("");
        try {
            const result = await dispatch(createStaffAccount({
                branchName: staffBranchName.trim(),
                email: staffEmail.trim(),
                password: staffPassword,
                whatsappNumber: staffWhatsApp.trim()
            })).unwrap();

            // Store info for success view
            setCreatedStaffInfo({
                branchName: staffBranchName.trim(),
                email: staffEmail.trim(),
                password: staffPassword
            });
            setStaffBranchName("");
            setStaffEmail("");
            setStaffPassword("");
            setStaffWhatsApp("");
            setCurrentView('success'); // Switch to success view
            // NOTE: Don't fetchStaffs here — it causes a cascade re-render
            // that resets the dialog. Staff list is refreshed when the dialog closes.
        } catch (err) {
            console.error("Error creating staff account:", err);
            setError(err || "Failed to create staff account");
        } finally {
            setActionLoading(false);
        }
    };

    const handleDeleteClick = (staff) => {
        setStaffToDelete(staff);
        setCurrentView('delete');
    };

    const confirmDelete = async () => {
        if (!staffToDelete) return;
        setActionLoading(true);
        setError("");
        try {
            await deleteStaff(staffToDelete.uid);
            toast.success("Staff member deleted successfully", {
                containerId: "bottom-center",
                position: "bottom-center",
            });
            setCurrentView('list');
            setStaffToDelete(null);
            dispatch(fetchStaffs(user.uid));
        } catch (err) {
            console.error("Error deleting staff:", err);
            toast.error(err.message || "Failed to delete staff member");
        } finally {
            setActionLoading(false);
        }
    };

    const handleShareCredentials = async () => {
        if (!createdStaffInfo) return;

        setSendingInvitations(false);
        setIsEmailLoading(true);

        // Hide loader and show "Sent" state after 3 seconds (optimistic UI)
        setTimeout(() => {
            setIsEmailLoading(false);
            setSendingInvitations(true);
        }, 3000);

        try {
            const { auth } = await import('../../firebase/firebaseConfig');
            const currentUser = auth.currentUser;
            if (!currentUser) throw new Error('No authenticated user');

            const token = await currentUser.getIdToken();
            const isLocal = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
            const backendUrl = isLocal ? 'http://localhost:5001' : (import.meta.env.VITE_BACKEND_URL || 'http://localhost:5001');

            const response = await fetch(`${backendUrl}/api/send-credentials`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
                body: JSON.stringify({
                    adminName: user?.displayName || 'Admin',
                    staffList: [{
                        email: createdStaffInfo.email,
                        password: createdStaffInfo.password,
                        branchName: createdStaffInfo.branchName,
                    }],
                }),
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.message || 'Failed to send emails');
            }

            toast.success("Credentials sent to staff member!");
        } catch (err) {
            console.error('Error sending credentials:', err);
            toast.error(err.message || 'Failed to send credential emails');
            setSendingInvitations(false);
        }
    };

    const textFieldStyles = {
        "& .MuiInputBase-root": {
            height: "44px",
            borderRadius: "8px",
            fontSize: "14px",
            backgroundColor: "#FFFFFF",
        },
        "& .MuiInputLabel-root": {
            fontSize: "14px",
            "&.MuiInputLabel-shrink": {
                backgroundColor: "#FFFFFF",
                padding: "0 6px",
            }
        }
    };

    return (
        <Dialog
            open={isOpen}
            onClose={onClose}
            maxWidth="xs"
            fullWidth
            PaperProps={{
                sx: {
                    width: (currentView === 'list' || currentView === 'success') ? "90vw" : "75vw",
                    maxWidth: (currentView === 'list' || currentView === 'success') ? "400px" : "340px",
                    borderRadius: "12px",
                    padding: "16px",
                    margin: "16px",
                    maxHeight: "85vh",
                    overflow: "hidden",
                    display: "flex",
                    flexDirection: "column"
                },
            }}
        >
            {/* List View */}
            {currentView === 'list' && (
                <>
                    <DialogTitle
                        sx={{
                            fontFamily: "Roboto",
                            fontWeight: "600",
                            fontSize: "18px",
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            padding: "0 0 8px 0",
                        }}
                    >
                        Manage Staff
                        <IconButton onClick={onClose} size="small">
                            <CloseIcon />
                        </IconButton>
                    </DialogTitle>

                    <DialogContent sx={{ padding: "8px 0", overflow: "auto", flex: 1 }}>
                        {loading ? (
                            <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
                                <CircularProgress size={30} />
                            </Box>
                        ) : staffs.length === 0 ? (
                            <Typography sx={{ textAlign: "center", py: 4, color: "#757575", fontSize: "14px" }}>
                                No staff members found.
                            </Typography>
                        ) : (
                            <List sx={{ pt: 0 }}>
                                {staffs.map((staff, index) => (
                                    <React.Fragment key={staff.uid}>
                                        <ListItem sx={{ px: 1, py: 1.5 }}>
                                            <ListItemText
                                                primary={
                                                    <Typography sx={{ fontWeight: 600, fontSize: "14px", color: "#212121" }}>
                                                        {staff.email}
                                                    </Typography>
                                                }
                                                secondary={
                                                    <Typography sx={{ fontSize: "12px", color: "#757575" }}>
                                                        Joined {staff.linkedAt ? new Date(staff.linkedAt).toLocaleDateString() : "N/A"}
                                                    </Typography>
                                                }
                                            />
                                            <ListItemSecondaryAction>
                                                <IconButton
                                                    edge="end"
                                                    size="small"
                                                    color="error"
                                                    onClick={() => handleDeleteClick(staff)}
                                                >
                                                    <DeleteIcon fontSize="small" />
                                                </IconButton>
                                            </ListItemSecondaryAction>
                                        </ListItem>
                                        {index < staffs.length - 1 && <Divider component="li" />}
                                    </React.Fragment>
                                ))}
                            </List>
                        )}
                    </DialogContent>

                    <DialogActions sx={{ padding: "16px 0 0 0", gap: 1.5, display: "flex" }}>
                        <Button
                            onClick={onClose}
                            variant="outlined"
                            sx={{
                                flex: 1,
                                height: "36px",
                                borderRadius: "6px",
                                fontSize: "14px",
                                borderColor: "#B00020",
                                color: "#B00020",
                                textTransform: "none",
                                "&:hover": {
                                    backgroundColor: "inherit",
                                    color: "#B00020",
                                    boxShadow: 2
                                }
                            }}
                        >
                            Close
                        </Button>
                        <Button
                            variant="contained"
                            onClick={() => setCurrentView('add')}
                            sx={{
                                flex: 1,
                                height: "36px",
                                borderRadius: "6px",
                                fontSize: "14px",
                                backgroundColor: "#0063F7 !important", // Keep !important for enabled state
                                textTransform: "none",
                                boxShadow: "none",
                                "&:hover": {
                                    backgroundColor: "#0051d4 !important",
                                },
                            }}
                        >
                            Create Staff
                        </Button>
                    </DialogActions>
                </>
            )}

            {/* Add Staff View */}
            {currentView === 'add' && (
                <>
                    <DialogTitle sx={{ p: 0, mb: 2, fontSize: "16px", fontWeight: 600 }}>
                        Create Staff
                    </DialogTitle>
                    <DialogContent sx={{ p: 0 }}>
                        <Typography variant="body2" sx={{ mb: 2, color: "#666" }}>
                            Enter the email address and password of the staff member you want to link.
                        </Typography>
                        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            <TextField
                                fullWidth
                                label="Branch Name *"
                                size="small"
                                value={staffBranchName}
                                onChange={(e) => setStaffBranchName(e.target.value)}
                                sx={textFieldStyles}
                                autoFocus
                                InputLabelProps={{ shrink: true }}
                            />
                            <TextField
                                fullWidth
                                label="Staff Email *"
                                size="small"
                                value={staffEmail}
                                onChange={(e) => setStaffEmail(e.target.value)}
                                sx={textFieldStyles}
                                InputLabelProps={{ shrink: true }}
                            />
                            <TextField
                                fullWidth
                                label="Password *"
                                type={showPassword ? "text" : "password"}
                                size="small"
                                value={staffPassword}
                                onChange={(e) => setStaffPassword(e.target.value)}
                                sx={textFieldStyles}
                                InputLabelProps={{ shrink: true }}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton onClick={() => setShowPassword(!showPassword)} edge="end" size="small">
                                                {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                }}
                            />
                            <TextField
                                fullWidth
                                label="WhatsApp Number (Optional)"
                                size="small"
                                placeholder="10-digit number"
                                value={staffWhatsApp}
                                onChange={(e) => {
                                    const val = e.target.value.replace(/\D/g, '');
                                    if (val.length <= 10) setStaffWhatsApp(val);
                                }}
                                sx={textFieldStyles}
                                InputLabelProps={{ shrink: true }}
                            />
                        </Box>
                    </DialogContent>
                    <DialogActions sx={{ p: 0, mt: 3, gap: 1.5 }}>
                        <Button
                            onClick={() => {
                                setCurrentView('list');
                                setStaffEmail("");
                                setStaffPassword(""); // Clear password
                                setError("");
                            }}
                            disabled={actionLoading}
                            variant="outlined"
                            sx={{
                                flex: 1,
                                height: "36px",
                                borderRadius: "6px",
                                fontSize: "14px",
                                borderColor: "#B00020",
                                color: "#B00020",
                                textTransform: "none",
                                "&:hover": {
                                    backgroundColor: "inherit",
                                    color: "#B00020",
                                    boxShadow: 2
                                }
                            }}
                        >
                            Back
                        </Button>
                        <Button
                            onClick={handleCreateStaff}
                            variant="contained"
                            disabled={actionLoading || !staffBranchName || !staffEmail || !staffPassword || staffPassword.length < 6} // Disable logic
                            sx={{
                                flex: 1,
                                height: "36px",
                                borderRadius: "6px",
                                fontSize: "14px",
                                backgroundColor: (actionLoading || !staffBranchName || !staffEmail || !staffPassword || staffPassword.length < 6) ? "rgba(0, 0, 0, 0.12) !important" : "#0063F7 !important", // Conditional styling
                                color: (actionLoading || !staffBranchName || !staffEmail || !staffPassword || staffPassword.length < 6) ? "rgba(0, 0, 0, 0.26) !important" : "#fff",
                                textTransform: "none",
                                boxShadow: "none",
                                "&:hover": {
                                    backgroundColor: (actionLoading || !staffBranchName || !staffEmail || !staffPassword || staffPassword.length < 6) ? "rgba(0, 0, 0, 0.12) !important" : "#0051d4 !important",
                                }
                            }}
                        >
                            {actionLoading ? <CircularProgress size={20} color="inherit" /> : "Create"}
                        </Button>
                    </DialogActions>
                </>
            )}

            {/* Success View */}
            {currentView === 'success' && (
                <>
                    <Box sx={{ textAlign: 'center', py: 2 }}>
                        <Box sx={{
                            width: 64, height: 64, mx: 'auto', mb: 2,
                            borderRadius: '50%', backgroundColor: '#E8F5E9',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}>
                            <CheckCircleIcon sx={{ fontSize: 40, color: '#4CAF50' }} />
                        </Box>
                        <Typography sx={{ fontFamily: 'Roboto', fontWeight: "600", fontSize: '18px', mb: 1, color: '#212121' }}>
                            Created Successfully!
                        </Typography>
                        <Typography sx={{ fontFamily: 'Roboto', fontSize: '13px', color: '#666', mb: 2, px: 2, lineHeight: 1.5 }}>
                            Share these credentials with your staff to start managing policies.
                        </Typography>

                        {createdStaffInfo && (
                            <Box sx={{
                                textAlign: 'left', mb: 3, mx: 1
                            }}>
                                <Typography variant="caption" sx={{ fontWeight: 600, mb: 0.5, color: '#212121', display: 'block' }}>
                                    Staff Credentials:
                                </Typography>
                                <Box sx={{
                                    p: 1.5,
                                    borderRadius: '8px',
                                    backgroundColor: '#F5F5F5',
                                    border: '1px solid #E0E0E0',
                                    position: 'relative',
                                }}>
                                    <IconButton
                                        size="small"
                                        onClick={() => {
                                            const text = `Branch: ${createdStaffInfo.branchName}\nEmail: ${createdStaffInfo.email}\nPassword: ${createdStaffInfo.password}`;
                                            navigator.clipboard.writeText(text);
                                            toast.info("Credentials copied");
                                        }}
                                        sx={{
                                            position: 'absolute',
                                            top: 4,
                                            right: 4,
                                            color: '#757575',
                                        }}
                                    >
                                        <ContentCopyIcon sx={{ fontSize: 14 }} />
                                    </IconButton>
                                    <Typography variant="body2" sx={{ fontWeight: 600, color: '#212121', fontSize: '13px' }}>
                                        {createdStaffInfo.branchName}
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: '#616161', display: 'block', fontSize: '11px' }}>
                                        Email: {createdStaffInfo.email}
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: '#616161', display: 'block', fontSize: '11px' }}>
                                        Password: {createdStaffInfo.password}
                                    </Typography>
                                </Box>
                            </Box>
                        )}

                        <Box sx={{ display: 'flex', gap: 1.5, mt: 2 }}>
                            <Button
                                onClick={() => {
                                    setCreatedStaffInfo(null);
                                    setSendingInvitations(false);
                                    setIsEmailLoading(false);
                                    dispatch(clearStaffCache(user.uid));
                                    dispatch(fetchStaffs(user.uid));
                                    setCurrentView('list');
                                }}
                                variant="outlined"
                                sx={{
                                    flex: 1,
                                    height: "40px",
                                    borderRadius: "8px",
                                    borderColor: "#B00020",
                                    color: "#B00020",
                                    textTransform: "none",
                                    fontSize: "14px",
                                    "&:hover": {
                                        backgroundColor: "inherit",
                                        borderColor: "#B00020"
                                    }
                                }}
                            >
                                Close
                            </Button>
                            <Button
                                variant="contained"
                                startIcon={isEmailLoading ? <CircularProgress size={16} color="inherit" /> : (sendingInvitations ? null : <EmailIcon sx={{ fontSize: 18 }} />)}
                                onClick={handleShareCredentials}
                                disabled={sendingInvitations || isEmailLoading}
                                sx={{
                                    flex: 1,
                                    height: "40px",
                                    borderRadius: "8px",
                                    backgroundColor: sendingInvitations ? '#4CAF50 !important' : (isEmailLoading ? '#BDBDBD !important' : '#0063F7 !important'),
                                    color: '#FFFFFF !important',
                                    textTransform: "none",
                                    fontSize: "14px",
                                    boxShadow: "none",
                                    '&:hover': {
                                        backgroundColor: sendingInvitations ? '#4CAF50 !important' : (isEmailLoading ? '#BDBDBD !important' : '#0051d4 !important'),
                                    },
                                    '&.Mui-disabled': {
                                        backgroundColor: sendingInvitations ? '#4CAF50 !important' : (isEmailLoading ? '#BDBDBD !important' : '#0063F7 !important'),
                                        color: '#FFFFFF !important'
                                    }
                                }}
                            >
                                {isEmailLoading ? 'Sharing...' : (sendingInvitations ? 'Sent ✓' : 'Email')}
                            </Button>
                        </Box>
                    </Box>
                </>
            )}

            {/* Delete Staff View */}
            {currentView === 'delete' && (
                <>
                    <DialogTitle sx={{ p: 0, mb: 1, fontSize: "16px", fontWeight: 600 }}>Delete Staff</DialogTitle>
                    <DialogContent sx={{ p: 0 }}>
                        <Typography sx={{ fontSize: "14px", color: "#212121" }}>
                            Are you sure you want to delete <b>{staffToDelete?.email}</b>? This will permanently remove their account and all associated data.
                        </Typography>
                    </DialogContent>
                    <DialogActions sx={{ p: 0, mt: 3, gap: 1.5 }}>
                        <Button
                            onClick={() => {
                                setCurrentView('list');
                                setStaffToDelete(null);
                            }}
                            disabled={actionLoading}
                            variant="outlined"
                            sx={{
                                flex: 1,
                                height: "36px",
                                borderRadius: "6px",
                                fontSize: "14px",
                                borderColor: "#B00020",
                                color: "#B00020",
                                textTransform: "none",
                                "&:hover": {
                                    backgroundColor: "inherit",
                                    color: "#B00020",
                                    boxShadow: 2
                                }
                            }}
                        >
                            Back
                        </Button>
                        <Button
                            onClick={confirmDelete}
                            color="error"
                            variant="contained"
                            disabled={actionLoading}
                            sx={{
                                flex: 1,
                                height: "36px",
                                borderRadius: "6px",
                                fontSize: "14px",
                                backgroundColor: "#B00020 !important",
                                textTransform: "none",
                                boxShadow: "none"
                            }}
                        >
                            {actionLoading ? <CircularProgress size={20} color="inherit" /> : "Delete"}
                        </Button>
                    </DialogActions>
                </>
            )}
        </Dialog>
    );
};

export default MobileStaffDialog;
